import React, { useState } from 'react';
import { X, Check, Calendar, Phone, ExternalLink, CreditCard, Lock, Loader2 } from 'lucide-react';
import { notifyError } from '../lib/notify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * UpgradeModal - Shows upgrade options with proper payment gating
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: function
 * - userTier: string (current user tier: 'self_serve', 'kickstart', 'full_coordination')
 * - userEmail: string (for pre-filling Cal.com)
 * - userName: string (for pre-filling Cal.com)
 */
const UpgradeModal = ({ isOpen, onClose, userTier = 'self_serve', userEmail = '', userName = '' }) => {
  const [loading, setLoading] = useState(null); // Track which button is loading

  if (!isOpen) return null;

  // Determine if user has paid for 1k+ tier
  const hasPaidKickstart = userTier === 'kickstart' || userTier === 'relocation_kickstart';
  const hasPaidFullCoordination = userTier === 'full_coordination' || userTier === 'concierge';
  const hasPaid1kPlus = hasPaidKickstart || hasPaidFullCoordination;

  // Cal.com links with pre-filled user info
  const getCalLink = (baseLink) => {
    const params = new URLSearchParams();
    if (userName) params.set('name', userName);
    if (userEmail) params.set('email', userEmail);
    const queryString = params.toString();
    return queryString ? `${baseLink}?${queryString}` : baseLink;
  };

  // Handle payment initiation
  const handlePayment = async (tierKey, amount) => {
    setLoading(tierKey);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/portal/upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tier: tierKey,
          amount: amount,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        notifyError(data, 'Failed to create checkout session.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      notifyError('Failed to initiate payment. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const upgradeOptions = [
    {
      key: 'kickstart',
      name: 'Relocation Kickstart',
      price: '$1,500',
      priceNum: 1500,
      description: 'Perfect for those who want expert guidance on specific aspects of their move.',
      features: [
        '2-3 strategy sessions with your Mundalo advisor',
        'Personalized action plan',
        'Direct WhatsApp access for 30 days',
        'Visa & housing roadmap'
      ],
      calLink: 'https://cal.com/mundalo/kickstart-consultation',
      highlight: false,
      isPaid: hasPaidKickstart || hasPaidFullCoordination // Full coord includes kickstart
    },
    {
      key: 'full_coordination',
      name: 'Full Coordination',
      price: '$7,500',
      priceNum: 7500,
      description: 'White-glove service - Your Mundalo advisor handles everything until you\'re settled.',
      features: [
        'Unlimited sessions until settled',
        'Your Mundalo advisor manages your entire move',
        'Property search & transaction support',
        'Direct access via WhatsApp & phone'
      ],
      calLink: 'https://cal.com/mundalo/full-coordination',
      highlight: true,
      isPaid: hasPaidFullCoordination
    }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="upgrade-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#111] border border-[#FFFFFF] max-w-3xl w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-[#666] hover:text-white transition"
          data-testid="close-upgrade-modal"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-8">
          <h2 className="text-2xl text-white font-light mb-2">Upgrade Your Plan</h2>
          <p className="text-[#888] text-sm">Get hands-on guidance from your Mundalo advisor for your Mexico relocation.</p>
          {!hasPaid1kPlus && (
            <p className="text-[#2CA8A0] text-xs mt-2">
              <Lock className="w-3 h-3 inline mr-1" />
              Payment required before scheduling calls
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {upgradeOptions.map((option) => (
            <div 
              key={option.name}
              className={`p-6 border rounded-lg ${option.highlight ? 'border-[#2CA8A0]/50 bg-[#2CA8A0]/5' : 'border-[#FFFFFF] bg-[#F9F6EF]'}`}
            >
              {option.highlight && (
                <span className="text-[10px] font-bold px-2 py-1 bg-[#B8860B] text-white rounded uppercase mb-3 inline-block">
                  White Glove
                </span>
              )}
              {option.isPaid && (
                <span className="text-[10px] font-bold px-2 py-1 bg-[#22c55e] text-white rounded uppercase mb-3 inline-block ml-2">
                  Paid
                </span>
              )}
              <h3 className="text-lg font-medium text-white mb-1">{option.name}</h3>
              <p className="text-2xl text-[#2CA8A0] font-light mb-3">{option.price}</p>
              <p className="text-[#888] text-sm mb-4">{option.description}</p>
              
              <ul className="space-y-2 mb-6">
                {option.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-[#888]">
                    <Check className="w-4 h-4 text-[#2CA8A0] shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Show different button based on payment status */}
              {option.isPaid ? (
                // PAID: Show Schedule Call button
                <a
                  href={getCalLink(option.calLink)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full flex items-center justify-center gap-2 py-3 text-sm font-medium transition ${
                    option.highlight 
                      ? 'bg-[#2CA8A0] text-black hover:bg-[#2CA8A0]' 
                      : 'bg-[#FFFFFF] text-white hover:bg-[#222] border border-[#333]'
                  }`}
                  data-testid={`schedule-btn-${option.key}`}
                >
                  <Calendar className="w-4 h-4" />
                  Schedule Call
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                // NOT PAID: Show Complete Payment button
                <button
                  onClick={() => handlePayment(option.key, option.priceNum)}
                  disabled={loading === option.key}
                  className={`w-full flex items-center justify-center gap-2 py-3 text-sm font-medium transition ${
                    option.highlight 
                      ? 'bg-[#2CA8A0] text-black hover:bg-[#2CA8A0] disabled:opacity-50' 
                      : 'bg-[#FFFFFF] text-white hover:bg-[#222] border border-[#333] disabled:opacity-50'
                  }`}
                  data-testid={`pay-btn-${option.key}`}
                >
                  {loading === option.key ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      Complete Payment to Schedule
                    </>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Info message for Self Serve users */}
        {!hasPaid1kPlus && (
          <div className="mt-6 p-4 bg-[#FFFFFF] border border-[#333] rounded-lg">
            <p className="text-[#888] text-sm text-center">
              <Lock className="w-4 h-4 inline mr-2 text-[#2CA8A0]" />
              Call scheduling is available after purchasing a Kickstart or Full Coordination package.
              Your current plan includes unlimited AI chat support.
            </p>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-[#666] text-xs mb-3">Questions before upgrading? Contact us directly:</p>
          <a 
            href="https://wa.me/14692131070" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#2CA8A0] hover:text-white text-sm transition"
          >
            <Phone className="w-4 h-4" /> WhatsApp your advisor
          </a>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
